import { Button, Input, Table, Tabs, Tag, Tooltip } from "antd";
import {
	ClockCircleOutlined,
	DollarOutlined,
	EditOutlined,
	EyeOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import "bootstrap-icons/font/bootstrap-icons.css";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	countLoan,
	loadAllLoan,
	loadLoanByStatus,
} from "../../redux/rtk/features/loan/loanSlice";
import getUserFromToken from "../../utils/getUserFromToken";
import ColVisibilityDropdown from "../Shared/ColVisibilityDropdown";
const { TabPane } = Tabs;

// My Loan Requests Table Component
function MyLoanRequestsTable({ list, total, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);
	const employeeId = getUserFromToken();

	const columns = [
		{
			title: "Transaction Type",
			key: "requestType",
			dataIndex: "requestType",
			render: (type) => (
				<Tag color="blue">{type === "Loan" ? "Loan" : "Advance"}</Tag>
			),
		},
		{
			title: "Amount (₹)",
			dataIndex: "loanAmount",
			key: "loanAmount",
			render: (amount) => {
				if (!amount) return "-";
				return `₹ ${Number(amount).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Purpose",
			dataIndex: "purposeCategory",
			key: "purposeCategory",
			render: (purpose) =>
				purpose
					? purpose.substring(0, 20) + (purpose.length > 20 ? "..." : "")
					: "-",
		},
		{
			title: "Returning Months",
			dataIndex: "returningMonths",
			key: "returningMonths",
			render: (months) => (months ? `${months} months` : "-"),
		},
		{
			title: "Requested Date",
			dataIndex: "requestedDate",
			key: "requestedDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Status",
			dataIndex: "approvalStatus",
			key: "approvalStatus",
			render: (approvalStatus) => {
				if (approvalStatus === "APPROVED") {
					return <Tag color="green">Approved</Tag>;
				} else if (approvalStatus === "REJECTED") {
					return <Tag color="red">Rejected</Tag>;
				} else {
					return <Tag color="orange">Pending</Tag>;
				}
			},
		},
		{
			title: "Actions",
			key: "action",
			render: ({ id }) => (
				<Button
					type="default"
					icon={<EyeOutlined />}
					onClick={() => {
						window.location.href = `/admin/loan/${id}`;
					}}
				>
					View
				</Button>
			),
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.LoanId || index,
			serial: index + 1,
		}));

	const filteredData = addKeys(list);

	return (
		<div>
			<div className="flex my-2 justify-between mb-4">
				<div className="w-50">
					<h4 className="text-2xl mb-2">My Loan Requests</h4>
				</div>
				<div className="flex justify-end mr-4">
					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						type="default"
					>
						Refresh
					</Button>
				</div>
			</div>

			<div
				style={{
					marginBottom: "20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "10px",
				}}
			>
				<ColVisibilityDropdown
					options={columns}
					columns={columns}
					columnsToShowHandler={columnsToShowHandler}
				/>
			</div>

			<Table
				className="text-center"
				scroll={{ x: true }}
				loading={!list}
				pagination={{
					defaultPageSize: 20,
					pageSizeOptions: [10, 20, 50, 100, 200],
					showSizeChanger: true,
					total: total || 0,
					onChange: (page, limit) => {
						dispatch(loadAllLoan({ page, limit }));
					},
				}}
				columns={columnsToShow}
				dataSource={filteredData}
			/>
		</div>
	);
}

// My Active Loans Table Component
function MyActiveLoansTable({ list, total, onRefresh }) {
	const dispatch = useDispatch();
	const [columnsToShow, setColumnsToShow] = useState([]);

	const columns = [
		{
			title: "Loan ID",
			dataIndex: "loanId",
			key: "loanId",
			render: (id) => id || "-",
		},
		{
			title: "Transaction Type",
			key: "requestType",
			dataIndex: "requestType",
			render: (type) => (
				<Tag color="blue">{type === "Loan" ? "Loan" : "Advance"}</Tag>
			),
		},
		{
			title: "Amount (₹)",
			dataIndex: "loanAmount",
			key: "loanAmount",
			render: (amount) => {
				if (!amount) return "-";
				return `₹ ${Number(amount).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Returning Months",
			dataIndex: "returningMonths",
			key: "returningMonths",
			render: (months) => (months ? `${months} months` : "-"),
		},
		{
			title: "Start Date",
			dataIndex: "startDate",
			key: "startDate",
			render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
		},
		{
			title: "Balance (₹)",
			dataIndex: "balance",
			key: "balance",
			render: (balance) => {
				if (balance === undefined || balance === null) return "-";
				return `₹ ${Number(balance).toLocaleString("en-IN")}`;
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			render: () => <Tag color="green">Active</Tag>,
		},
		{
			title: "Actions",
			key: "action",
			render: ({ id }) => (
				<div style={{ display: "flex", gap: "8px" }}>
					<Tooltip title="EMI History">
						<Button
							type="default"
							icon={<ClockCircleOutlined />}
							size="small"
							onClick={() => {
								console.log("EMI History for loan:", id);
							}}
						>
							EMI History
						</Button>
					</Tooltip>
					<Tooltip title="View Details">
						<Button
							type="default"
							icon={<EyeOutlined />}
							size="small"
							onClick={() => {
								window.location.href = `/admin/loan/${id}`;
							}}
						>
							View
						</Button>
					</Tooltip>
				</div>
			),
		},
	];

	useEffect(() => {
		setColumnsToShow(columns);
	}, []);

	const columnsToShowHandler = (val) => {
		setColumnsToShow(val);
	};

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((i, index) => ({
			...i,
			key: i.id || i.LoanId || index,
			serial: index + 1,
			loanId: i.id || i.LoanId || i.loanId,
			balance: i.balance || i.Balance || i.loanAmount || i.loanAmount,
		}));

	const filteredData = addKeys(list);

	return (
		<div>
			<div className="flex my-2 justify-between mb-4">
				<div className="w-50">
					<h4 className="text-2xl mb-2">My Active Loans</h4>
				</div>
				<div className="flex justify-end mr-4">
					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						type="default"
					>
						Refresh
					</Button>
				</div>
			</div>

			<div
				style={{
					marginBottom: "20px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "10px",
				}}
			>
				<ColVisibilityDropdown
					options={columns}
					columns={columns}
					columnsToShowHandler={columnsToShowHandler}
				/>
			</div>

			<Table
				className="text-center"
				scroll={{ x: true }}
				loading={!list}
				pagination={{
					defaultPageSize: 20,
					pageSizeOptions: [10, 20, 50, 100, 200],
					showSizeChanger: true,
					total: total || 0,
					onChange: (page, limit) => {
						dispatch(loadAllLoan({ page, limit }));
					},
				}}
				columns={columnsToShow}
				dataSource={filteredData}
			/>
		</div>
	);
}

const MyLoan = (props) => {
	const dispatch = useDispatch();
	const [activeTab, setActiveTab] = useState("requests");
	const employeeId = getUserFromToken();
	const list = useSelector((state) => state.loan?.list || []);
	const total = useSelector((state) => state.loan?.total || 0);

	// Filter loans for current user
	const myLoans = list.filter(
		(loan) =>
			loan.employeeId === employeeId ||
			loan.EmployeeId === employeeId ||
			loan.userId === employeeId
	);

	// Filter active loans (approved loans for current user)
	const myActiveLoans = myLoans.filter(
		(loan) => loan.approvalStatus === "APPROVED" || loan.status === "ACTIVE"
	);

	// Filter loan requests (all loans for current user)
	const myLoanRequests = myLoans;

	useEffect(() => {
		if (employeeId) {
			dispatch(loadAllLoan());
			dispatch(countLoan());
		}
	}, [dispatch, employeeId]);

	const handleRefresh = () => {
		dispatch(loadAllLoan());
	};

	return (
		<div>
			<div className="card card-custom">
				<div className="card-body">
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						type="card"
					>
						<TabPane tab="My Loan Requests" key="requests">
							<MyLoanRequestsTable
								list={myLoanRequests}
								total={myLoanRequests.length}
								onRefresh={handleRefresh}
							/>
						</TabPane>
						<TabPane tab="My Active Loans" key="active">
							<MyActiveLoansTable
								list={myActiveLoans}
								total={myActiveLoans.length}
								onRefresh={handleRefresh}
							/>
						</TabPane>
					</Tabs>
				</div>
			</div>
		</div>
	);
};

export default MyLoan;

